import { Ajax } from "../ajax/ajax.js";
import { ListView } from "../components/list-view.js";
import { Table } from "../components/table.js";
import { Browser } from "../core/browser.js";
import { Dialog } from "../dialog/dialog.js";

const ARIA_LABEL_SELECTOR = "a[href], audio, video[controls], input, select, button, textarea, header, footer, nav, main, aside, section, form, [role='navigation'], iframe, img";

export class Razor {
	static #_onStateUpdate = null;
	static #stateUpdated = false;

	static OnThemeChange = null;

	static get OnStateUpdate() {
		return Razor.#_onStateUpdate;
	}

	static set OnStateUpdate(value) {
		if (Razor.#stateUpdated) {
			value?.();
		}

		Razor.#_onStateUpdate = value;
	}

	static GetHandlerURL = handler => {
		const parsed = new URL(window.location);

		parsed.hash = "";
		parsed.searchParams.set("handler", handler);

		return parsed.toString();
	}

	static async GetPartialView(name, modelProperties, ajaxOptions = {}) {
		modelProperties ??= {};

		const response = await Ajax.Post(this.GetHandlerURL("RenderPartialView"), {
			queryParameters: {
				view: name
			},
			body: modelProperties,
			headers: {
				pragma: 'no-cache',
				'cache-control': 'no-cache'
			},
			LoadTimeout: 0,
			...ajaxOptions
		});

		return response?.ok ? response.content : "";
	}

	static RenderPartialView(name, modelProperties, container, options = { LoadingIcon: true }) {
		return new Promise(resolve => {
			if (!container) { resolve(); }

			setTimeout(() => {
				if (options.LoadingIcon) {
					container.innerHTML = '<div class="pv-loading-container"><div class="pv-loading-icon"></div></div>';
				}
			}, 300);

			this.GetPartialView(name, modelProperties, options).then(response => {
				options.LoadingIcon = false;
				container.innerHTML = response;

				this.UpdateState();
				resolve();
			});
		});
	}

	static #DisableButton(button) {
		if (button) {
			button.disabled = true;
			button.classList.add("disabled");
		}
	}

	static #EnableButton(button) {
		if (button) {
			button.removeAttribute("disabled");
			button.removeClass("disabled");
		}
	}

	static #FireButtonAjax(button) {
		let buttonForm = button.form;

		if (button?.nodeName !== "BUTTON") {
			buttonForm = document.getElementById(button.form) ?? button.closest("form");
		}

		function send() {
			if (!button?.ajax?.SkipFormValidation && (buttonForm && ([...buttonForm.querySelectorAll("chip-form") ?? []].find(x => !x.reportValidity()) || !buttonForm.reportValidity()))) {
				return;
			} else {
				Razor.#DisableButton(button);
			}

			const body = new URLSearchParams();

			if (buttonForm) {
				for (const pair of new FormData(buttonForm)) {
					if (pair[0] !== "__RequestVerificationToken") {
						body.append(pair[0].slice(pair[0].indexOf(".") + 1), pair[1]);
					}
				}
			}

			if (button?.CheckValid?.(body) === false) {
				return Razor.#EnableButton(button);
			}

			const responseOverride = button?.ajax?.success ?? {};

			responseOverride.ok ??= response => {
				history.replaceState("", document.title, button.ajax?.ReturnURL ?? response.url);
				location.reload();
			}

			responseOverride["400"] ??= response => {
				if (typeof button.ajax?.OnBadRequest === "function") {
					button.ajax.OnBadRequest(response);
				} else {
					Dialog.ShowWarning("That doesn't look right", Razor.GetBadRequestReason(response));
				}
			}

			let url = "";

			if (button.ajax?.Handler) {
				url = Razor.GetHandlerURL(button.ajax.Handler);
			} else {
				url = button.getAttribute("formAction") ? button.formAction : Razor.GetHandlerURL("Save");
			}

			Ajax.Post(url, {
				body,
				OnComplete: response => {
					Razor.#EnableButton(button);

					if (response?.ok) {
						window.onbeforeunload = null;
					}
				},
				success: responseOverride,
				error: error => {
					Razor.#EnableButton(button);

					Dialog.ShowError("Error", "Something went wrong, and we're not sure what it was. You can either try again or report this error to an administrator if it persists.", error);
				}, ...button.ajax
			});
		}

		if (button.dataset.requiresConfirmation === "true") {
			Dialog.ShowConfirmation(button?.ajax?.ConfirmationData?.Title ?? "Are you sure?", button?.ajax?.ConfirmationData?.Body ?? "Are you sure you want to do this?").then(result => {
				if (result) {
					send();
				}
			});
		} else {
			send();
		}
	}

	static GetBadRequestReason = response => Browser.TryParseJSON(response.content)?.at(0)?.value.errors.at(0).errorMessage ?? response.content;

	static UpdateThemeToggleText(theme) {
		if (window.btnThemeToggle) {
			btnThemeToggle.tooltip = Localizer[`THEME_BUTTON_${theme.toUpperCase()}`];
		}
	}

	static #RefreshSidebarTooltips() {
		const nav = document.querySelector(".sidebar--collapsible");

		nav?.querySelectorAll(".sidebar-button").forEach(x => {
			x.setAttribute("tooltip-placement", "right");
			x.setAttribute("tooltip", document.documentElement.classList.contains("sidebar--collapsed") ? x.textContent.trim() : "");
		});
	}

	static RefreshTooltips() {
		document.querySelectorAll("[tooltip]").forEach(element => {
			if (element.__tooltipID) { return; }

			element.__tooltipID ??= `tt${crypto.randomUUID().slice(0, 8)}`;

			let showTimeout,
				hideTimeout;

			if (element.matches?.(ARIA_LABEL_SELECTOR) && !element.hasAttribute("aria-label") && element.textContent.trim() === "") {
				element.ariaLabel = element.getAttribute("tooltip");
			}

			element.onmouseover = ev => {
				const dropdown = ev.target.closest(".dropdown__list"),
					tooltip = element.getAttribute("tooltip");

				if (!document.getElementById(element.__tooltipID) && (!dropdown || dropdown.contains(element))) {
					clearTimeout(hideTimeout);
					hideTimeout = null;

					showTimeout ??= setTimeout(() => {
						if (tooltip?.trim() !== "") {
							let { width, left, top, height, right } = element.getBoundingClientRect(),
								container = document.createElementWithContents("div",
									`
								<div class="tooltip-content">
									${tooltip}
								</div>
								<div class="tooltip-arrow">
								</div>
							`.trim(),
									{
										className: "tooltip-container",
										id: element.__tooltipID,
										role: "tooltip"
									}
								);

							if (!height && !width) { return; }

							top += window.scrollY;
							right += window.scrollX;
							left += window.scrollX;

							const PLACEMENT_FNS = {
								placeTop: () => {
									Object.assign(container.style, {
										left: `${left + (width / 2)}px`,
										top: `${top}px`
									});

									container.setAttribute("placement", "top");
								},
								placeRight: () => {
									Object.assign(container.style, {
										left: `${left + width}px`,
										top: `${top + (height / 2)}px`
									});

									container.setAttribute("placement", "right");
								},
								placeBottom: () => {
									Object.assign(container.style, {
										top: `${top + height}px`
									});

									container.setAttribute("placement", "bottom");
								},
								placeLeft: () => {
									Object.assign(container.style, {
										left: `${left}px`,
										top: `${top + (height / 2)}px`
									});

									container.setAttribute("placement", "left");
								}
							}

							PLACEMENT_FNS.placeTop();

							element.setAttribute("aria-describedby", element.__tooltipID);
							document.body.appendChild(container);

							const tooltipDimensions = container.getBoundingClientRect(),
								currentDirection = element.getAttribute("tooltip-direction")?.toLowerCase() ?? "top";

							if (currentDirection !== "top") {
								PLACEMENT_FNS[`place${currentDirection[0].toUpperCase() + currentDirection.slice(1).toLowerCase()}`]();
							}

							if (tooltipDimensions.top <= 0 || tooltipDimensions.left <= 0 || (right + ((tooltipDimensions.width - width) / 2) > window.innerWidth)) {
								if (right + ((tooltipDimensions.width - width) / 2) <= window.innerWidth) {
									PLACEMENT_FNS.placeRight();
								} else if (tooltipDimensions.right <= window.innerWidth) {
									PLACEMENT_FNS.placeBottom();
								} else {
									PLACEMENT_FNS.placeLeft();
								}
							}

							container.classList.add("tooltip--shown");
						}
					}, 300);
				}
			}

			function hideTooltip() {
				clearTimeout(showTimeout);
				showTimeout = null;

				hideTimeout ??= setTimeout(() => {
					document.getElementById(element.__tooltipID)?.remove();
				}, 300);
			}

			element.onmouseout = ev => {
				const dropdown = ev.target.closest(".dropdown__list");

				if (dropdown?.contains(element) === false || !element.contains(ev.relatedTarget)) {
					hideTooltip();
				}
			}

			element.onmouseleave = () => hideTooltip();
		});
	}

	static InvokeThemeChange() {
		const target = document.querySelector("link[href*='code-highlight']")

		if (target) {
			if (document.documentElement.dataset.theme === "dark") {
				target.href = target.href.replace("code-highlight.css", "code-highlight-dark.css");
			} else {
				target.href = target.href.replace("code-highlight-dark.css", "code-highlight.css");
			}
		}

		Razor.OnThemeChange?.();
	}

	static #RefreshElements() {
		document.querySelectorAll(".standard-pagination-input").forEach(x => x.onkeyup = ev => {
			if (ev.key === "Enter" && !isNaN(ev.target.value) && ev.target.value.trim() !== "") {
				Browser.SetURLParameter("p", Math.max(1, parseInt(ev.target.value)), true);
			}
		});

		document.querySelectorAll(".btn-collapse-sidebar").forEach(x => x.onclick = () => {
			const nav = x.closest(".sidebar--collapsible");

			if (nav) {
				nav.querySelectorAll("[data-bs-toggle='collapse']:not(.collapsed)").forEach(x => x.click());
				document.documentElement.toggleClass("sidebar--collapsed");

				window.localStorage.setItem("collapseSidebar", document.documentElement.classList.contains("sidebar--collapsed"));

				this.#RefreshSidebarTooltips();
			}
		});

		this.#RefreshSidebarTooltips();

		if (window.btnThemeToggle) {
			btnThemeToggle.onclick = () => {
				const theme = document.documentElement.dataset.theme === "dark" ? "light" : "dark";

				document.documentElement.dataset.theme = theme;
				localStorage.setItem("site-theme", theme);

				this.UpdateThemeToggleText(theme);
				this.InvokeThemeChange();
			}

			this.UpdateThemeToggleText(document.documentElement.dataset.theme);
		}

		document.querySelectorAll("[data-enhance='true']").forEach(x => x.enhance());

		document.querySelectorAll(".cookie-warning__button--accept").forEach(button => button.onclick = () => {
			document.cookie = `CookiePolicyCheck=null;path=/;expires=${Browser.AddYears(new Date(), 1)}`;
			document.querySelector(".header__cookie-warning")?.classList.add("d-none");
		});

		document.querySelectorAll(".btn-entity-post").forEach(x => x.onclick = () => this.#FireButtonAjax(x));

		document.querySelectorAll(".search-bar").forEach(bar => {
			const input = bar.querySelector("input");

			if (input) {
				function submit() {
					if (bar.Search?.href) {
						const url = Object.assign(new URL(Browser.ResolveRelativeURL(bar.Search?.href)), {
							hash: ""
						});

						url.searchParams.set("q", input.value.trim() === "" ? null : input.value.trim());

						location.href = url;
					} else {
						Browser.SetURLParameter("q", input.value.trim() === '' ? null : input.value.trim());
					}
				}

				input.onkeyup = ev => {
					if (ev.key === "Enter") {
						submit();
					}
				}

				bar.querySelector(".search-bar__button").onclick = () => submit();
			}
		});
	}

	static #RefreshPopovers() {
		const popovers = [...document.querySelectorAll("[data-bs-toggle='popover']")].filter(x => !bootstrap.Popover.getInstance(x));

		popovers.forEach(popover => {
			const fadeItem = popover.closest(".fade-item"),
				eventType = popover.dataset.bsTrigger === "hover" ? "mouseenter" : "click";

			popover.addEventListener(eventType, () => {
				bootstrap.Popover.getOrCreateInstance(popover).show();

				if (eventType === "mouseenter") {
					popover.keepOpen = true;
				}
			});

			if (eventType === "mouseenter") {
				popover.onmouseleave = () => popover.keepOpen = false;
			}

			popover.addEventListener("inserted.bs.popover", () => {
				const domElement = document.getElementById(popover.getAttribute("aria-describedby"));

				fadeItem?.classList.add("show");
				domElement.querySelector("input")?.focus();

				domElement.onmouseenter = () => popover.keepOpen = true;
				domElement.onmouseleave = () => popover.keepOpen = false;

				domElement.querySelectorAll("button").forEach(btn => btn.addEventListener("click", () => bootstrap.Popover.getOrCreateInstance(popover).hide()));
			});

			popover.addEventListener("hide.bs.popover", ev => {
				if (popover.keepOpen) {
					ev.preventDefault();
				} else {
					fadeItem?.classList?.remove("show");
				}
			});

			if (eventType === "mouseenter") {
				popover.addEventListener("shown.bs.popover", ev => {
					document.getElementById(ev.target.getAttribute("aria-describedby"))?.addEventListener("mouseleave", event => {
						if (!ev.target.contains(event.relatedTarget) && ev.target !== event.relatedTarget) {
							bootstrap.Popover.getOrCreateInstance(popover).hide();
						}
					});
				});
			}

			bootstrap.Popover.getOrCreateInstance(popover, {
				html: true,
				delay: {
					hide: "200"
				},
				sanitize: false,
				content: function () {
					const wrapper = popover.nextElementSibling.cloneNode(true);
					wrapper.removeClass("d-none");

					return wrapper.outerHTML;
				}
			})
		});

		function onBodyClick(ev) {
			if (!ev.target?.closest("[data-bs-toggle='popover'],.popover") || ev.target?.matches("[data-bs-dismiss='popover']")) {
				popovers.forEach(x => bootstrap.Popover.getOrCreateInstance(x).hide());
			}
		}

		document.body.removeEventListener("click", onBodyClick);
		document.body.addEventListener("click", onBodyClick);
	}

	static #RefreshComponents() {
		Table.RefreshTables();
		ListView.RefreshListViews();
	}

	static UpdateState() {
		this.#RefreshComponents();

		this.#RefreshElements();
		this.#RefreshPopovers();

		this.RefreshTooltips();

		Razor.OnStateUpdate?.();
		Razor.#stateUpdated = true;
	}

	static SetButtonAjax(button, data) {
		if (button) {
			button.ajax ??= {};
			Object.assign(button.ajax, data);
		}
	}
}

Dialog.OnOpened = () => Razor.UpdateState();